import { Fragment } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  HiOutlineChatBubbleLeftRight,
  HiOutlinePhone,
  HiOutlineAcademicCap,
  HiOutlineHomeModern,
} from "react-icons/hi2";

import s from "./StaticPage.module.css";
import helpHero from "../../images/s1.jpg";
import { bankName, email, countryCode } from "../../util/config";

const Help = () => {
  return (
    <Fragment>
      <Helmet>
        <title>Help &amp; support — {bankName}</title>
        <meta
          name="description"
          content={`Cost-of-living support, mortgage guidance, and how to reach ${bankName} when you need us.`}
        />
        <link rel="canonical" href="/help" />
      </Helmet>
      <section className={s.page}>
        <div
          className={s.hero}
          style={{ backgroundImage: `url(${helpHero})` }}
        >
          <div className={s.heroOverlay} />
          <div className={s.heroContent}>
            <p className={s.eyebrow}>Help centre</p>
            <h1>We are here when your finances feel uncertain</h1>
            <p className={s.lead}>
              Practical guidance, compassionate support teams, and clear routes
              to specialist help—whether you are managing bills, studying, or
              protecting your home.
            </p>
          </div>
        </div>

        <div className={s.metrics}>
          <div className={s.metric}>
            <span className={s.metricValue}>24/7</span>
            <span className={s.metricLabel}>Secure messaging &amp; fraud lines</span>
          </div>
          <div className={s.metric}>
            <span className={s.metricValue}>{countryCode}</span>
            <span className={s.metricLabel}>Dedicated support teams</span>
          </div>
          <div className={s.metric}>
            <span className={s.metricValue}>Free</span>
            <span className={s.metricLabel}>Money guidance signposting</span>
          </div>
        </div>

        <div className={s.section}>
          <div className={s.sectionHeader}>
            <h2>How we can support you</h2>
            <p className={s.sectionIntro}>
              Early conversation almost always leads to better outcomes. If you
              are worried about payments, tell us—we have trained colleagues
              and structured options under industry good practice.
            </p>
          </div>
          <div className={s.grid}>
            <div className={s.card}>
              <div className={s.cardIcon}>
                <HiOutlineChatBubbleLeftRight aria-hidden />
              </div>
              <h3>Money worries &amp; payment difficulty</h3>
              <p>
                Missed or late payments can happen during illness, job change,
                or family shock. We review income and expenditure with you,
                explore breathing space where appropriate, and document any
                temporary arrangement in writing.
              </p>
            </div>
            <div className={s.card}>
              <div className={s.cardIcon}>
                <HiOutlineHomeModern aria-hidden />
              </div>
              <h3>Mortgages &amp; rising rates</h3>
              <p>
                If you are concerned about interest changes, we explain
                product switches, term adjustments, and government schemes you
                may qualify for—without pressure to borrow more than you need.
              </p>
            </div>
            <div className={s.card}>
              <div className={s.cardIcon}>
                <HiOutlineAcademicCap aria-hidden />
              </div>
              <h3>Students &amp; cost of living</h3>
              <p>
                Budget templates, bursary timing, and tips for shared housing
                costs—plus links to university hardship funds and national debt
                charities when independent advice is best.
              </p>
            </div>
            <div className={s.card}>
              <div className={s.cardIcon}>
                <HiOutlinePhone aria-hidden />
              </div>
              <h3>Talk to us directly</h3>
              <p>
                Email{" "}
                <a href={`mailto:${email}`} className={s.inlineLink}>
                  {email}
                </a>{" "}
                or use secure messaging in online banking for account-specific
                queries. For card loss or suspected fraud, use the numbers in
                your app—we prioritise those calls.
              </p>
            </div>
          </div>
        </div>

        <div className={s.split}>
          <div className={s.splitText}>
            <h2>Before hardship becomes crisis</h2>
            <p>
              We work with accredited advisory bodies and other FINMA-approved
              organisations. If you need independent budgeting help, we will
              pause collections activity where rules allow while you seek
              advice—so you can stabilise without fear of immediate enforcement.
            </p>
            <p>
              Vulnerable customers can request a dedicated contact, longer
              call-back windows, and alternative formats (large print, audio) so
              information is never a barrier to support.
            </p>
          </div>
          <div className={s.splitText}>
            <div className={s.highlight}>
              <p>
                <strong>Explore products with confidence.</strong> Our{" "}
                <Link to="/borrowing" className={s.inlineLink}>
                  borrowing
                </Link>
                ,{" "}
                <Link to="/banking" className={s.inlineLink}>
                  banking
                </Link>
                , and{" "}
                <Link to="/insurance" className={s.inlineLink}>
                  insurance
                </Link>{" "}
                pages explain features in plain English—so you can compare
                before you apply.
              </p>
            </div>
          </div>
        </div>

        <p className={s.trustStrip}>
          {bankName} follows FINMA regulatory guidance and Swiss banking
          standards. If you disagree with a decision, ask for a review and we
          will outline our complaints procedure—including referral to the Swiss
          Banking Ombudsman if unresolved.
        </p>
      </section>
    </Fragment>
  );
};

export default Help;
